import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';

import splitPintListText, { countLength } from './Service';
import EasyApplyView from './EasyApplyView';
import { onSingleJobCardClicked } from '@core/components/amplitude/global-job-cards';

/**
 * Reusable Job Card specially made for studysmarter jobboard
 * @param {object} props - props object
 * @param {string} props.logoUrl - logo url
 * @param {string} props.jobTitle - job title
 * @param {string} props.companyName - company name
 * @param {object} props.pinLists - pin lists, contains jobLocations, jobSalary, jobType
 * @param {string} props.postedDate - posted date
 * @returns {element} - JobCards element
 */
function JobCard({
	logoUrl = '',
	jobURL = '',
	jobTitle = '',
	companyName = '',
	pinLists = [],
	postedDate = '',
	isExternalLink = false,
	isEasyApply = false,
	loader = false,
}) {
	const externalLinkConfig = {
		rel: 'external nofollow',
		target: '_blank',
	};

	return (
		<div className={`c-job-card ${loader ? 'skeleton' : ''}`}>
			<a
				href={jobURL}
				className='c-job-card__job-link'
				{...(isExternalLink ? externalLinkConfig : '')}
				onClick={() => onSingleJobCardClicked('job_search', isExternalLink)}
			>
				<div className='c-job-card__root-content-block'>
					<div className='c-job-card__intro d-flex'>
						<div className='c-job-card__logo-block flex-grow-0 flex-shrink-0 flex-basis-auto'>
							<img
								src={logoUrl}
								alt={`${companyName} logo`}
								className='c-job-card__logo'
								onError={(event) => {
									// eslint-disable-next-line no-param-reassign
									event.target.src = `${window.jobboardApp.themeUri}dist/assets/images/fallback-logo.svg`;
								}}
							/>
						</div>
						<div className='c-job-card__title-block'>
							<h4 className='c-job-card__title h4'>{jobTitle}</h4>
							<p className='c-job-card__company-name'>{companyName}</p>
						</div>
					</div>

					<PinList pinLists={pinLists} />

					<div className='d-flex column-gap-30 row-gap-10 flex-wrap'>
						<span className='c-job-card__posted-date ss-copy-small'>
							{postedDate}
						</span>
						{isEasyApply && (
							<div className='c-job-card__separator d-none d-md-block'></div>
						)}
						{isEasyApply && <EasyApplyView />}
					</div>
				</div>
			</a>
		</div>
	);
}

/**
 * Pin list component, contains jobLocations, jobSalary, jobType
 * @param {object} props - props object
 * @param {array} props.pinLists - array of pin lists
 * @returns {element} - PinList element
 */
function PinList({ pinLists = [] }) {
	if (pinLists.length === 0) {
		return null;
	}

	// remove empty item and string from array
	const filteredPinLists = pinLists.filter(
		(item) => item !== '' && typeof item === 'string',
	);

	return (
		<div className='c-job-card__pin-lists'>
			{filteredPinLists.map((pinList) => {
				const shorterPinList = splitPintListText(pinList);
				const pinListLength = countLength(pinList);
				return (
					<p className='c-job-card__pin-list' key={pinList}>
						{pinListLength < 4 ? (
							pinList
						) : (
							<ModifiedPintList pinList={pinList} />
						)}
					</p>
				);
			})}
		</div>
	);
}

function ModifiedPintList({ pinList }) {
	const [showMore, setShowMore] = useState(true);

	const shorterPinList = splitPintListText(pinList);
	const pinListLength = countLength(pinList);

	const OnToggleClicked = (e, state) => {
		e.preventDefault();
		setShowMore(state);
	};

	return (
		<>
			{showMore ? shorterPinList : pinList}
			{showMore ? (
				<>
					{`, `}
					<span
						onClick={(e) => OnToggleClicked(e, false)}
						className='c-job-card__show-more text-ocean-blue fs-15'
					>
						{__('Show more', 'job')} {pinListLength - 3}
					</span>
				</>
			) : (
				<>
					{` `}
					<span
						onClick={(e) => OnToggleClicked(e, true)}
						className='c-job-card__show-more text-ocean-blue fs-15'
					>
						{__('Show less', 'job')}
					</span>
				</>
			)}
		</>
	);
}
JobCard.propTypes = {
	logoUrl: PropTypes.string,
	jobTitle: PropTypes.string,
	companyName: PropTypes.string,
	pinLists: PropTypes.arrayOf(PropTypes.string),
	postedDate: PropTypes.string,
	jobURL: PropTypes.string,
	loader: PropTypes.bool,
	isExternalLink: PropTypes.bool,
};

// JobCard.defaultProps = {
// 	logoUrl: '',
// 	jobTitle: '',
// 	companyName: '',
// 	pinLists: {
// 		jobLocations: '',
// 		jobSalary: '',
// 		jobType: '',
// 	},
// 	postedDate: '',
// 	jobURL: '',
// 	loader: false,
// 	isExternalLink: false,
// };

PinList.propTypes = {
	pinLists: PropTypes.arrayOf(PropTypes.string),
};

// PinList.defaultProps = {
// 	pinLists: [],
// };

export default JobCard;
export { PinList, ModifiedPintList };
